import React, { useRef, useState, useMemo } from 'react'
import { View, FlatList, TouchableOpacity, Image, TextInput } from 'react-native'
import { RouteProp, useNavigation, useRoute, useTheme } from '@react-navigation/native'
import {
  Text,
  Icon,
  BottomModalBase,
  BackButtonWeb,
  KeyboardAwareView,
  Screen,
  Notes,
  HiddenText,
  GroupAvatar,
  MessageBubbleSkeletonList,
  Skeleton,
  ConversationWithNotesSkeleton,
  TranslatableText,
} from '@components'
import { iconApp } from '@assets'
import getStyle from './style'
import { GroupConversationParams } from './types'
import { GroupActionsModal, GroupMembersModal } from './components'
import { getCommonStyles, selectImage, formatPublishedAt, showApiErrorToast } from '@utils'
import { Pin } from '../components'
import { ConversationMessageWithFocus } from '../conversation'
import ActionItem from '../conversation/components/ActionItem'
import { RenderPinned } from '../components/RenderPinned'
import { MessagesStackParamList } from '@navigation'
import { useScreen } from '@hooks'
// Import EditNote directly (not via the `menu` barrel): the barrel re-exports
// every menu screen — map, camera, calendar… — dragging their native modules
// into anything that touches a conversation (and breaking Jest suites).
import { EditNote } from '../../../screens/menu/notes/EditNote'
import { useGetConversationById, usePaginateMessages, useGetProfile } from '@hooks'
import { useSendMessage } from '@hooks'
import type { Contact, Message } from '@hooks'
import { useLanguage } from '@context'

interface GroupConversationProps {
  params?: GroupConversationParams
}

export const GroupConversation: React.FC<GroupConversationProps> = (props) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const { t } = useLanguage()

  const flatListRef = useRef<FlatList>(null)
  const { isMobile, isDesktop, isWeb } = useScreen()
  const navigation = useNavigation()
  const route = useRoute<RouteProp<MessagesStackParamList, 'GroupConversation'>>()

  const { conversationId } = props.params || route.params

  // API hooks
  const { data: conversation, isLoading: isLoadingConversation } =
    useGetConversationById(conversationId)
  const { data: currentUser } = useGetProfile()
  const {
    flatData: apiMessages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchMessages,
  } = usePaginateMessages(conversationId)
  const sendMessageMutation = useSendMessage()

  const [messageText, setMessageText] = useState('')
  const [showActionsModal, setShowActionsModal] = useState(false)
  const [showMembersModal, setShowMembersModal] = useState(false)
  const [image, setImage] = useState<string>()
  const [messageId, setMessageId] = useState<string>()
  const [noteId, setNoteId] = useState<string>()
  const [isNoteShared, setIsNoteShared] = useState<boolean>(false)
  const [height, setHeight] = useState(40)
  const [focusedMessageId, setFocusedMessageId] = useState<string>()

  // Transform API messages to component format
  const messages: ConversationMessageWithFocus[] = useMemo(() => {
    if (!apiMessages || !currentUser) return []

    return apiMessages.map((msg: Message) => {
      const isSent = msg.author.id === currentUser.accountId
      return {
        id: msg.id,
        text: msg.content,
        image: undefined, // API doesn't support images yet
        timestamp: formatPublishedAt(msg.createdAt),
        isSent,
        senderId: msg.author.id,
        status: 'sent' as const,
        isPinned: false, // TODO: Add pin support from API
        isFocused: msg.id === focusedMessageId,
      }
    })
  }, [apiMessages, currentUser, focusedMessageId])

  const members = conversation?.participants || []
  // Transform participants to GroupMember format for modal
  const groupMembers = useMemo(() => {
    return members.map((p) => ({
      id: p.id,
      name: `${p.fullName || ''}`.trim() || 'Unknown',
      avatar: p.avatarUrl || '',
      isAdmin: p.role === 'administrator' || p.role === 'owner',
    }))
  }, [members])
  const pinnedMessages = messages.filter((m) => m.isPinned)

  // Couleur du groupe (violet/mauve comme dans l'image)
  const groupColors = {
    primary: '#FF69B4', // Rose
    light: '#FF69B4' + '20', // Rose avec transparence
  }

  const goBack = () => {
    navigation.goBack()
  }

  const renderMessage = ({ item }: { item: ConversationMessageWithFocus }) => {
    const messageAuthor = apiMessages?.find((m) => m.id === item.id)?.author
    const sender = members.find((p) => p.id === item.senderId)
    const senderName = sender
      ? `${sender.fullName || ''}`.trim() || 'Unknown'
      : messageAuthor?.fullName || 'Membre supprimé'

    return (
      <TouchableOpacity style={styles.messageContainer} onLongPress={() => setMessageId(item.id)}>
        {item.isSent ? (
          // Message envoyé
          <View style={styles.sentMessageWrapper}>
            <View style={styles.receivedMessageContainer}>
              {item.isPinned && <Pin isFocused={item.isFocused} color={groupColors.primary} />}
              <View style={[styles.sentMessage, { backgroundColor: groupColors.primary }]}>
                {!!item.image && <Image source={{ uri: item.image }} style={styles.messageImage} />}
                {!!item.text && (
                  <TranslatableText
                    text={item.text}
                    type="body"
                    textStyle={styles.sentMessageText}
                  />
                )}
              </View>
            </View>
            <Text type="bodyExtraSmall" style={styles.sentMessageTime}>
              {item.timestamp}
            </Text>
          </View>
        ) : (
          // Message reçu avec avatar et nom
          <View style={styles.receivedMessageWrapper}>
            <View style={styles.receivedMessageContainer}>
              <View style={styles.receivedMessage}>
                <Text type="bodySmallBold" style={styles.senderName}>
                  {senderName}
                </Text>
                <TranslatableText
                  text={item.text}
                  type="body"
                  textStyle={styles.receivedMessageText}
                />
              </View>
              {item.isPinned && <Pin isFocused={item.isFocused} color={groupColors.primary} />}
            </View>
            <View style={styles.messageFooter}>
              <Image
                source={
                  messageAuthor?.avatarUrl || sender?.avatarUrl
                    ? { uri: messageAuthor?.avatarUrl || sender?.avatarUrl || '' }
                    : iconApp
                }
                style={styles.messageAvatar}
              />
              <Text type="bodyExtraSmall" style={styles.receivedMessageTime}>
                {item.timestamp}
              </Text>
            </View>
          </View>
        )}
      </TouchableOpacity>
    )
  }

  const sendMessage = async () => {
    if ((messageText.trim() || image) && conversationId) {
      const content = messageText.trim()
      const currentImage = image

      // Clear input immediately
      setMessageText('')
      setImage(undefined)

      try {
        await sendMessageMutation.mutateAsync({
          conversationId,
          content,
          imageUri: currentImage,
        })
        refetchMessages()
        // Messages will be refetched automatically via query invalidation
      } catch (error) {
        // Restore message text on error
        setMessageText(content)
        if (currentImage) setImage(currentImage)
        showApiErrorToast(t, error)
      }
    }
  }

  const onAddImage = () => {
    selectImage(
      (uri) => {
        setImage(uri)
      },
      {
        title: t('photoAlert.title'),
        message: t('photoAlert.message'),
        cancel: t('common.cancel'),
        gallery: t('categories.gallery'),
        camera: t('categories.camera'),
      },
    )
  }

  const scrollToMessage = (id: string) => {
    const index = messages.findIndex((item) => item.id === id)
    if (index !== -1 && flatListRef.current) {
      setFocusedMessageId(id)
      flatListRef.current.scrollToIndex({ index, animated: true })
    }
  }

  const onAddNotePress = (isShared?: boolean) => {
    setNoteId('')
    if (isShared !== undefined) setIsNoteShared(isShared)
  }

  const handleNotePress = (noteId: string) => {
    setNoteId(noteId)
  }

  if (isLoadingConversation || !conversation) {
    if (isDesktop) {
      return (
        <Screen>
          <ConversationWithNotesSkeleton />
        </Screen>
      )
    }
    return (
      <Screen>
        <View style={commonStyles.flex}>
          <View style={styles.customHeader}>
            <Skeleton width={40} height={40} borderRadius={20} />
            <View style={{ marginLeft: theme.spacing.md, flex: 1 }}>
              <Skeleton
                width="60%"
                height={16}
                borderRadius={4}
                style={{ marginBottom: theme.spacing.xs }}
              />
              <Skeleton width="40%" height={12} borderRadius={4} />
            </View>
          </View>
          <MessageBubbleSkeletonList count={6} />
        </View>
      </Screen>
    )
  }

  // Safety check: render nothing without a conversation id. Placed AFTER every
  // hook so the hook order stays identical across renders (Rules of Hooks) —
  // it previously sat before the hooks and could throw "rendered fewer hooks".
  if (!conversationId) {
    return null
  }

  return (
    <Screen>
      <View
        style={
          isDesktop
            ? [commonStyles.rowNoGap, commonStyles.alignStart, commonStyles.fullHeight]
            : commonStyles.flex
        }
      >
        <KeyboardAwareView isFullScreen offsetTopInset>
          {!isMobile && route.params && <BackButtonWeb />}
          {/* Header personnalisé pour groupe */}
          <View style={[styles.customHeader, { borderBottomColor: groupColors.primary }]}>
            {isMobile && (
              <TouchableOpacity style={styles.backButton} onPress={goBack}>
                <Icon name="arrowBack" size={24} color={theme.colors.secondary} />
              </TouchableOpacity>
            )}

            <View style={styles.headerContent}>
              <View style={styles.headerLeft}>
                {/* Avatars multiples pour le groupe */}
                <GroupAvatar
                  profiles={members.map((m) => ({
                    id: m.id,
                    avatarUrl: m.avatarUrl || undefined,
                  }))}
                />
              </View>

              <View style={styles.headerInfo}>
                <Text type="bodyBold" style={styles.headerName}>
                  {conversation?.name || t('messagesScreen.group')}
                </Text>
                <Text type="bodyExtraSmall" style={styles.headerStatus}>
                  {t('common.memberCount', { count: members.length })}
                </Text>
              </View>
            </View>

            {/*<View style={styles.headerActions}>
            <TouchableOpacity style={styles.headerAction}>
              <Icon name="translate" size={20} color={theme.colors.secondary} />
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.headerAction}
              onPress={() => setShowActionsModal(true)}
            >
              <Icon name="moreVertical" size={20} color={theme.colors.secondary} />
            </TouchableOpacity>
          </View>*/}
          </View>

          {pinnedMessages.length > 0 && (
            <RenderPinned
              pinnedMessages={pinnedMessages}
              color={groupColors.light}
              onPress={scrollToMessage}
            />
          )}
          {/* Liste des messages */}
          <FlatList
            ref={flatListRef}
            data={messages}
            renderItem={renderMessage}
            keyExtractor={(item) => item.id}
            style={styles.messagesList}
            contentContainerStyle={styles.messagesContent}
            showsVerticalScrollIndicator={false}
            inverted
            onEndReached={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
              }
            }}
            onEndReachedThreshold={0.5}
          />

          {/* Input de saisie */}
          <View style={styles.inputContainer}>
            <HiddenText text={messageText} setHeight={setHeight} />
            <View style={styles.inputWrapper}>
              <TextInput
                style={[styles.textInput, isWeb && { height }]}
                value={messageText}
                onChangeText={setMessageText}
                placeholder={t('messaging.messagePlaceholder')}
                placeholderTextColor={theme.colors.greyBlue}
                multiline
              />
              <TouchableOpacity
                style={[styles.sendButton, { backgroundColor: groupColors.primary }]}
                onPress={sendMessage}
              >
                <Icon name="send" size={20} color={theme.colors.white} />
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={[styles.imageButton, { backgroundColor: groupColors.light }]}
              onPress={onAddImage}
            >
              {!!image && (
                <TouchableOpacity
                  style={styles.removeImageIcon}
                  onPress={() => setImage(undefined)}
                >
                  <Icon name="cross" size={15} color={theme.colors.white} />
                </TouchableOpacity>
              )}
              {!!image && (
                <Image source={{ uri: image }} style={{ width: '100%', height: '100%' }} />
              )}
              {!image && <Icon name="picture" size={20} color={groupColors.primary} />}
            </TouchableOpacity>
          </View>
        </KeyboardAwareView>
        {isDesktop && (
          <View style={[commonStyles.flex350, commonStyles.fullHeight]}>
            {noteId === undefined && (
              <Notes
                profileId={conversationId}
                onNotePress={handleNotePress}
                onAddPress={onAddNotePress}
              />
            )}
            {noteId !== undefined && (
              <EditNote
                noteId={noteId}
                isShared={isNoteShared}
                isCreationStack
                onBack={() => setNoteId(undefined)}
                onComplete={() => setNoteId(undefined)}
                contactId={conversationId}
              />
            )}
          </View>
        )}
      </View>

      {/* Modal d'actions de groupe */}
      <GroupActionsModal
        visible={showActionsModal}
        onClose={() => setShowActionsModal(false)}
        groupName={conversation?.name || ''}
        onShare={() => console.log('Partager le groupe')}
        onViewMembers={() => {
          setShowActionsModal(false)
          setShowMembersModal(true)
        }}
        onCustomizeGroup={() => console.log('Personnaliser le groupe')}
        onBlock={() => console.log('Bloquer le groupe')}
        onReport={() => console.log('Signaler le groupe')}
      />

      {/* Modal des membres du groupe */}
      <GroupMembersModal
        visible={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        members={groupMembers}
        onRemoveMember={(memberId) => {
          // TODO: Implement remove member API call
          console.log('Remove member:', memberId)
        }}
      />
      <BottomModalBase
        isVisible={!!messageId}
        setIsVisible={() => setMessageId(undefined)}
        hasCloseButton
        largePaddingTop={false}
      >
        <View style={styles.actionsContainer}>
          <ActionItem
            action={{
              label: messages?.find((m) => m.id === messageId)?.isPinned
                ? 'Désépingler'
                : 'Épingler',
              icon: 'pin',
              onPress: () => {
                // TODO: Implement pin/unpin API call
                console.log('Pin/unpin message:', messageId)
                setMessageId(undefined)
              },
            }}
          />
        </View>
      </BottomModalBase>
    </Screen>
  )
}
